/**
 * O1 — Per-side stat panel.
 *
 * Anchored to a viewport corner (left for A, right for B). Renders the
 * spectator-relevant MechState slice as: name strip → heat bar → zone
 * armor diagram (with optional numeric legend) → evasion pips row →
 * status badges row. Each subwidget self-hides when its data is
 * default (no badges, no crits, no evasion), so the panel naturally
 * tightens during early-match calm.
 */

import type { CSSProperties, ReactNode } from "react";
import type { MechState } from "@pilot-protocol/replay-parser";
import { Pip } from "@pilot-protocol/theme/chrome";
import { HeatBar } from "./HeatBar";
import { Panel } from "./Panel";
import { StatusBadges } from "./StatusBadges";
import { ZoneArmorDiagram } from "./ZoneArmorDiagram";
import { critCountsOf, statusFlagsOf } from "./derive";

export type SideStatAnchor = "left" | "right";

export interface SideStatPanelProps {
  mech: MechState;
  side: "A" | "B";
  /** Display name (consumer usually passes the agent name). */
  name: string;
  /** Side accent color override; defaults to the token. */
  accent?: string;
  /** Optional archetype hint, shown under the name. */
  archetype?: string;
  /** Which side of the viewport to anchor to. Defaults to A→left, B→right. */
  anchor?: SideStatAnchor;
  /** Show the numeric zone legend under the silhouette. */
  showZoneLegend?: boolean;
  /** Top offset in px — useful when the metadata strip is mounted. */
  topOffset?: number;
  /**
   * Cosmetic display slot — sits prominently below the header, above
   * the heat bar. Intended for an agent portrait, faction emblem,
   * voxel chassis thumbnail, or any other React node the consumer
   * wants to feature. Reserves `--pp-cosmetic-slot-h` height when
   * set; collapses entirely when omitted.
   *
   * Typical consumers:
   *   <VoxelDisplay archetype={...} matchSeed={...} />  // chassis portrait
   *   <img src="/emblems/sigma-corp.png" />              // 2D emblem
   *   <FactionBadge faction="orca" />                    // custom widget
   */
  cosmeticSlot?: ReactNode;
  /**
   * When true, render the dashed placeholder ring around an empty
   * cosmetic slot. When `cosmeticSlot` is undefined, the slot collapses;
   * when it's `null` and this flag is on, the dashed ring renders as an
   * explicit "no portrait equipped" affordance (used by the Storybook
   * bench). Audit fix — used to always render the dashed border even
   * when a real portrait filled the slot.
   */
  showEmptyCosmeticPlaceholder?: boolean;
  /**
   * Loadout slot — renders between the zone armor diagram and the
   * status badges. Consumer supplies a small list (weapons, abilities,
   * ammo counts). Collapses entirely when omitted so panels without
   * loadout data stay tight.
   */
  loadoutSlot?: ReactNode;
  /** Hide entirely. */
  hidden?: boolean;
}

const rootStyle = (anchor: SideStatAnchor, topOffset: number): CSSProperties => ({
  position: "absolute",
  top: topOffset,
  [anchor]: "var(--pp-edge-gutter)",
  width: "var(--pp-panel-width)",
  pointerEvents: "none",
  userSelect: "none",
});

const contentStyle: CSSProperties = {
  padding: "var(--pp-padding)",
  display: "flex",
  flexDirection: "column",
  gap: "var(--pp-gap)",
  color: "var(--pp-text)",
  fontFamily: "var(--pp-font-family)",
  fontSize: "var(--pp-font-md)",
  fontWeight: "var(--pp-weight-normal)",
};

const headerStyle = (accentColor: string): CSSProperties => ({
  display: "flex",
  alignItems: "center",
  gap: 8,
  paddingBottom: 6,
  borderBottom: `1px solid ${accentColor}`,
});

const sideTagStyle = (accentColor: string): CSSProperties => ({
  padding: "1px 6px",
  background: accentColor,
  color: "var(--pp-text-on-accent)",
  fontWeight: "var(--pp-weight-bold)",
  fontSize: "var(--pp-font-sm)",
  letterSpacing: "var(--pp-tracking-caps)",
});

const cosmeticSlotBaseStyle: CSSProperties = {
  height: "var(--pp-cosmetic-slot-h)",
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "rgba(0, 0, 0, 0.4)",
  overflow: "hidden",
  position: "relative",
};

const cosmeticSlotEmptyStyle: CSSProperties = {
  ...cosmeticSlotBaseStyle,
  border: "1px dashed var(--pp-border)",
};

const nameStyle: CSSProperties = {
  fontWeight: "var(--pp-weight-bold)",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  flex: 1,
  minWidth: 0,
};

const archetypeStyle: CSSProperties = {
  fontSize: "var(--pp-font-sm)",
  textTransform: "uppercase",
  letterSpacing: "var(--pp-tracking-caps)",
  color: "var(--pp-text-muted)",
};

const loadoutSlotStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
  paddingTop: 4,
  borderTop: "1px solid var(--pp-border)",
  fontSize: "var(--pp-font-sm)",
  color: "var(--pp-text)",
};

const evasionRowStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  fontSize: "var(--pp-font-sm)",
  color: "var(--pp-text-muted)",
};

const evasionPipsStyle: CSSProperties = {
  display: "flex",
  gap: 3,
};

const EVASION_MAX_DISPLAY = 5;

function defaultAnchor(side: "A" | "B"): SideStatAnchor {
  return side === "A" ? "left" : "right";
}

function defaultAccent(side: "A" | "B"): string {
  return side === "A" ? "var(--pp-accent-a)" : "var(--pp-accent-b)";
}

/**
 * Default top offset = strip height (40px) + stack gap (12px) = 52px.
 * MatchOverlay passes its own computed value when the metadata strip is
 * actually mounted; the default covers the strip-mounted case for
 * standalone consumers. Audit fix — the prior default of 56 didn't
 * match anything; now derived from the same numbers MatchOverlay uses.
 */
const DEFAULT_TOP_OFFSET = 52;

export function SideStatPanel({
  mech,
  side,
  name,
  accent,
  archetype,
  anchor = defaultAnchor(side),
  showZoneLegend = false,
  topOffset = DEFAULT_TOP_OFFSET,
  cosmeticSlot,
  showEmptyCosmeticPlaceholder = false,
  loadoutSlot,
  hidden = false,
}: SideStatPanelProps) {
  if (hidden) return null;
  const accentColor = accent ?? defaultAccent(side);
  const flags = statusFlagsOf(mech);
  const crits = critCountsOf(mech);
  const evasionCount = Math.min(EVASION_MAX_DISPLAY, Math.max(0, mech.evasion_pips));
  // Render the cosmetic slot when the consumer passed something (even
  // null, an explicit "I want the empty placeholder visible" signal).
  // Slot styling: dashed ring only when there's no body to wrap.
  const hasCosmeticBody = cosmeticSlot !== undefined && cosmeticSlot !== null;
  const renderCosmeticSlot = hasCosmeticBody || showEmptyCosmeticPlaceholder;
  const cosmeticStyle = hasCosmeticBody ? cosmeticSlotBaseStyle : cosmeticSlotEmptyStyle;

  return (
    <div style={rootStyle(anchor, topOffset)} aria-label={`Side ${side} stats`}>
      <Panel accent={accentColor}>
        <div style={contentStyle}>
          <div style={headerStyle(accentColor)}>
            <span style={sideTagStyle(accentColor)}>{side}</span>
            <span style={nameStyle} title={name}>
              {name}
            </span>
            {archetype ? <span style={archetypeStyle}>{archetype}</span> : null}
          </div>

          {renderCosmeticSlot ? (
            <div style={cosmeticStyle}>{cosmeticSlot}</div>
          ) : null}

          <HeatBar heat={mech.heat} accent={accentColor} />

          <ZoneArmorDiagram mech={mech} accent={accentColor} showLegend={showZoneLegend} />

          {loadoutSlot ? <div style={loadoutSlotStyle}>{loadoutSlot}</div> : null}

          {mech.evasion_pips > 0 ? (
            <div style={evasionRowStyle} aria-label={`Evasion ${mech.evasion_pips}`}>
              <span>EVADE</span>
              <span style={evasionPipsStyle}>
                {Array.from({ length: EVASION_MAX_DISPLAY }, (_, i) => (
                  <Pip key={i} color={accentColor} filled={i < evasionCount} />
                ))}
              </span>
            </div>
          ) : null}

          <StatusBadges flags={flags} crits={crits} />
        </div>
      </Panel>
    </div>
  );
}
